import React from 'react';
import cn from 'class-names';

import Button from '@uikit/Form/Button/Button';
import PayPalIcon from '@uikit/old/Icon/socialSvg/PayPalIcon';
import { BillingPublisherPaymentMethod } from 'respona_api/generated/common_pb';

import { BillingPublisherPayoutSettingsType } from '@ts/billing-publisher.types';

import { directDepositImg, wiseImg } from '../../constants';
import { getPayoutMethodLabel } from '../../utils';

import './BillingDetailsListCard.scss';

type Props = {
  paymentSettings: BillingPublisherPayoutSettingsType[];
  isHighlighted: boolean;
  onUpdate: () => void;
};

const resolveAccountLabel = (
  pm: BillingPublisherPayoutSettingsType
): { detailsLabel: string; accountLabel: React.ReactNode; icon: React.ReactNode } => {
  switch (pm.paymentMethod) {
    case BillingPublisherPaymentMethod.PAYPAL:
      return {
        detailsLabel: 'Accounting email',
        accountLabel: pm.payPal?.email ?? '',
        icon: <PayPalIcon />,
      };
    case BillingPublisherPaymentMethod.BANK_ACCOUNT:
      return {
        detailsLabel: 'Bank account',
        accountLabel: pm.bankAccount?.accountSummary || '—',
        icon: (
          <img
            src={directDepositImg}
            alt=""
            aria-hidden="true"
            className="billing-details-list-card__method-icon"
          />
        ),
      };
    case BillingPublisherPaymentMethod.WISE_ACCOUNT:
      return {
        detailsLabel: 'Wise account email',
        accountLabel: pm.wiseAccount?.accountSummary || '—',
        icon: (
          <img
            src={wiseImg}
            alt=""
            aria-hidden="true"
            className="billing-details-list-card__method-icon"
          />
        ),
      };
    case BillingPublisherPaymentMethod.MANUAL:
      return {
        detailsLabel: 'Details',
        accountLabel: pm.manual?.description ?? '',
        icon: null,
      };
    default:
      return { detailsLabel: 'Details', accountLabel: '—', icon: null };
  }
};

function BillingDetailsListCard({ paymentSettings, isHighlighted, onUpdate }: Props): JSX.Element {
  const hasMethods = paymentSettings.length > 0;

  return (
    <div className="billing-details-list-card">
      <div className="billing-details-list-card__header">
        <span>Billing details</span>
        {hasMethods ? (
          <Button
            className={cn('billing-details-list-card__update-btn', {
              'billing-details-list-card__update-btn--highlighted': isHighlighted,
            })}
            leftIcon="refresh"
            colorIcon={isHighlighted ? '#FFFFFF' : '#3424EB'}
            type="blue"
            size="s"
            onClick={onUpdate}
          >
            Update billing details
          </Button>
        ) : (
          <Button type="primary" size="s" onClick={onUpdate}>
            + Add billing details
          </Button>
        )}
      </div>

      <div
        className={cn('billing-details-list-card__body', {
          'billing-details-list-card__body--empty': !hasMethods,
        })}
      >
        {hasMethods ? (
          paymentSettings.map((pm) => {
            const { detailsLabel, accountLabel, icon } = resolveAccountLabel(pm);
            return (
              <React.Fragment key={pm.paymentMethod}>
                <div className="billing-details-list-card__row">
                  <div className="billing-details-list-card__row-label">Payment method</div>
                  <div className="billing-details-list-card__row-value">
                    {icon}
                    {getPayoutMethodLabel(pm)}
                  </div>
                </div>
                <div className="billing-details-list-card__row">
                  <div className="billing-details-list-card__row-label">{detailsLabel}</div>
                  <div className="billing-details-list-card__row-value">{accountLabel}</div>
                </div>
              </React.Fragment>
            );
          })
        ) : (
          <div className="billing-details-list-card__empty">
            <p>Please add your billing details for withdrawals.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default BillingDetailsListCard;
